import * as React from 'react';

import { Badge, type BadgeProps } from '../primitives/badge';
import { cn } from '../lib/utils';

/**
 * ISA-88 equipment operational states.
 */
export type EquipmentState =
  | 'Idle'
  | 'Running'
  | 'Paused'
  | 'Held'
  | 'Stopped'
  | 'Stopping'
  | 'Complete'
  | 'Aborting'
  | 'Aborted'
  | 'Error'
  | 'Maintenance'
  | 'Offline';

/**
 * ISA-95 work order / batch lifecycle states.
 */
export type WorkOrderStatus =
  | 'Draft'
  | 'Created'
  | 'Scheduled'
  | 'Released'
  | 'InProgress'
  | 'Paused'
  | 'Completed'
  | 'Closed'
  | 'Cancelled';

export type StatusValue = EquipmentState | WorkOrderStatus | string;

/**
 * Map ISA-88/ISA-95 status strings to Badge variants.
 * Unknown values fall back to `muted`.
 */
const statusToVariant: Record<string, BadgeProps['variant']> = {
  // ISA-88 equipment states
  Idle: 'muted',
  Running: 'success',
  Paused: 'warning',
  Held: 'warning',
  Stopped: 'critical',
  Stopping: 'warning',
  Complete: 'success',
  Aborting: 'critical',
  Aborted: 'critical',
  Error: 'critical',
  Maintenance: 'info',
  Offline: 'muted',
  // ISA-95 work order states
  Draft: 'muted',
  Created: 'muted',
  Scheduled: 'info',
  Released: 'info',
  InProgress: 'success',
  Completed: 'success',
  Closed: 'secondary',
  Cancelled: 'destructive',
};

export interface StatusBadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The ISA-88 / ISA-95 status value */
  status: StatusValue;
  /** Optional explicit variant override */
  variant?: BadgeProps['variant'];
  /** Show an indicator dot before the label */
  showDot?: boolean;
  /** Animate the dot (for critical/active states) */
  pulse?: boolean;
  /**
   * Translated text to DISPLAY in place of the raw status token.
   *
   * The badge's tone stays keyed on `status` — the ISA-88 / ISA-95 token the
   * API sends — while the operator reads their own language. Without this the
   * only way to show a German label was to pass a German string as `status`,
   * which would silently drop the tone mapping (no `Laufend` in
   * `statusToVariant`) and make every localized badge grey.
   */
  label?: string;
}

/**
 * Status badge for manufacturing state values.
 * Automatically maps ISA-88 equipment states and ISA-95 work order states to
 * appropriate badge variants using the design tokens.
 *
 * @example
 * ```tsx
 * <StatusBadge status="Running" />
 * <StatusBadge status="Error" pulse />
 * <StatusBadge status="InProgress" showDot={false} />
 * ```
 */
export const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(
  (
    { status, variant, showDot = true, pulse = false, label, className, ...props },
    ref
  ) => {
    const resolvedVariant =
      variant ?? statusToVariant[status] ?? 'muted';

    return (
      <Badge
        ref={ref}
        variant={resolvedVariant}
        className={cn('gap-1.5', className)}
        {...props}
      >
        {showDot && (
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full bg-current',
              pulse && 'animate-pulse'
            )}
            aria-hidden
          />
        )}
        <span>{label ?? status}</span>
      </Badge>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';
